import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Lock,
  Unlock,
  Target,
  Calendar,
  Clock,
  BookOpen,
  Sparkles,
  CheckCircle2,
  Play,
  Trophy,
  BrainCircuit,
  Zap,
  TrendingUp,
  Award,
  ChevronRight,
  User,
  BarChart3,
  MapPin,
  MessageCircle,
  ArrowRight,
  Mail,
} from 'lucide-react';
import { ErrorState } from '@/components/common';
import { learningPlanApi } from '@/services/learningManagementApi';
import { profileApi } from '@/services/profileApi';
import { getLearningPathDayDisplayLabel } from '@/utils/learningPathDisplayUtils';
import type { LearningPlan } from '@/types/learningManagement';
import type { UserProfile } from '@/types/userProfile';

const PLAN_STARTED_KEY = 'five_ai_learning_path_started_';
const DAY_UNLOCKED_KEY = 'five_ai_learning_path_day_unlocked_';

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes

const LOADING_MESSAGES = [
  'Creating your profile and bio...',
  'Building your personalized learning path...',
  'This usually takes 1–2 minutes...',
  'Curating content just for you...',
  'Almost there...',
];

export const UserLearningPathPage: React.FC = () => {
  const { userEmail: encodedEmail } = useParams<{ userEmail: string }>();
  const navigate = useNavigate();
  const [learningPath, setLearningPath] = useState<LearningPlan | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const [planStarted, setPlanStarted] = useState(false);
  const [unlockedDays, setUnlockedDays] = useState<Set<string>>(new Set());
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set());
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef(0);

  const userEmail = useMemo(() => {
    if (encodedEmail) {
      try {
        return decodeURIComponent(encodedEmail);
      } catch {
        return encodedEmail;
      }
    }
    return null;
  }, [encodedEmail]);

  // Rotate loading messages
  useEffect(() => {
    if (!isLoading && !isPolling) return;
    const t = setInterval(() => {
      setLoadingMessageIndex((i) => (i + 1) % LOADING_MESSAGES.length);
    }, 3500);
    return () => clearInterval(t);
  }, [isLoading, isPolling]);

  // Fetch learning path and profile; poll when path is still generating
  useEffect(() => {
    if (!userEmail) {
      setError(new Error('User email is required'));
      setIsLoading(false);
      return;
    }

    const startedKey = `${PLAN_STARTED_KEY}${userEmail}`;
    const unlockedKey = `${DAY_UNLOCKED_KEY}${userEmail}`;

    const applyPathState = (pathData: LearningPlan | null) => {
      if (!pathData) return;
      setLearningPath(pathData);
      const isStarted = localStorage.getItem(startedKey) === 'true';
      setPlanStarted(isStarted);
      try {
        const unlocked = localStorage.getItem(unlockedKey);
        if (unlocked) {
          setUnlockedDays(new Set(JSON.parse(unlocked)));
        } else if (isStarted) {
          const initialUnlocked = new Set(['module-0-day-0']);
          setUnlockedDays(initialUnlocked);
          localStorage.setItem(unlockedKey, JSON.stringify(Array.from(initialUnlocked)));
        }
      } catch (e) {
        console.warn('Failed to load unlocked days', e);
      }
    };

    startTimeRef.current = Date.now();

    const fetchData = async (): Promise<boolean> => {
      try {
        const [pathData, profileData] = await Promise.all([
          learningPlanApi.getByUser(userEmail).catch((e) => {
            console.warn('Learning path fetch failed:', e);
            return null;
          }),
          profileApi.getById(userEmail).catch((e) => {
            console.warn('Profile fetch failed:', e);
            return null;
          }),
        ]);

        if (profileData) {
          setProfile(profileData);
        }
        if (pathData) {
          applyPathState(pathData);
          setIsLoading(false);
          setIsPolling(false);
          setError(null);
          if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current);
            pollTimeoutRef.current = null;
          }
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return true;
        }
        return false;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load learning path'));
        setIsLoading(false);
        setIsPolling(false);
        return true;
      }
    };

    (async () => {
      setIsLoading(true);
      setError(null);
      startTimeRef.current = Date.now();
      const gotPath = await fetchData();
      if (gotPath) return;

      setIsPolling(true);
      setIsLoading(false);

      pollTimeoutRef.current = setTimeout(() => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsPolling(false);
        setError(new Error('Learning path is still being generated. Please check back in a few minutes.'));
      }, POLL_TIMEOUT_MS);

      pollIntervalRef.current = setInterval(async () => {
        if (Date.now() - startTimeRef.current >= POLL_TIMEOUT_MS) return;
        await fetchData();
      }, POLL_INTERVAL_MS);
    })();

    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [userEmail]);

  const handleStartPlan = () => {
    if (!userEmail) return;
    
    setPlanStarted(true);
    const startedKey = `${PLAN_STARTED_KEY}${userEmail}`;
    localStorage.setItem(startedKey, 'true');
    
    // Unlock first day
    const firstDayKey = 'module-0-day-0';
    const unlocked = new Set([firstDayKey]);
    setUnlockedDays(unlocked);
    const unlockedKey = `${DAY_UNLOCKED_KEY}${userEmail}`;
    localStorage.setItem(unlockedKey, JSON.stringify(Array.from(unlocked)));
    
    // TODO: Call API to mark plan as started
  };

  const isDayUnlocked = useCallback((moduleIdx: number, dayIdx: number) => {
    if (!planStarted) return false;
    const dayKey = `module-${moduleIdx}-day-${dayIdx}`;
    return unlockedDays.has(dayKey);
  }, [planStarted, unlockedDays]);

  const toggleDay = (moduleIdx: number, dayIdx: number) => {
    const dayKey = `module-${moduleIdx}-day-${dayIdx}`;
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }
      return next;
    });
  };

  const toggleModule = (index: number) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Calculate derived values - MUST be before early returns (Rules of Hooks)
  // Use actual dailyContents count so all 7 days from API are reflected (not module.totalDays which may be wrong)
  const totalDays = useMemo(() => {
    if (!learningPath?.learningModules?.length) return 0;
    const fromContents = learningPath.learningModules.reduce(
      (sum, mod) => sum + (Array.isArray(mod.dailyContents) ? mod.dailyContents.length : 0),
      0
    );
    if (fromContents > 0) return fromContents;
    return learningPath.learningModules.reduce(
      (sum, mod) => sum + (mod.totalDays || 0),
      0
    ) || (learningPath.estimatedDurationHours
      ? Math.ceil((learningPath.estimatedDurationHours || 0) / 8)
      : 7);
  }, [learningPath]);

  const totalModules = useMemo(() => {
    return learningPath?.learningModules?.length || 0;
  }, [learningPath]);

  // Calculate all days across modules for timeline
  const allDays = useMemo(() => {
    if (!learningPath || !learningPath.learningModules) {
      return [];
    }
    
    const days: Array<{
      moduleIdx: number;
      dayIdx: number;
      dayNumber: number;
      title?: string;
      description?: string;
      day: {
        id?: string;
        title?: string;
        description?: string;
        order?: number;
        unlocked?: boolean;
        isMilestone?: boolean;
        estimatedDurationMinutes?: number;
        lessonIds?: string[];
        nudgeIds?: string[];
        tipIds?: string[];
        hackIds?: string[];
        tidbitIds?: string[];
        nanoIds?: string[];
        dayNumber?: number;
        assessmentId?: string;
        learningObjectives?: string[];
      };
      isUnlocked: boolean;
    }> = [];
    let globalDayNumber = 1;
    
    try {
      learningPath.learningModules.forEach((module, moduleIdx) => {
        if (module && module.dailyContents && Array.isArray(module.dailyContents)) {
          module.dailyContents.forEach((day, dayIdx) => {
            if (day) {
              days.push({
                moduleIdx,
                dayIdx,
                dayNumber: globalDayNumber++,
                title: day.title,
                description: day.description,
                day,
                isUnlocked: isDayUnlocked(moduleIdx, dayIdx),
              });
            }
          });
        }
      });
    } catch (e) {
      console.error('Error calculating allDays:', e);
      return [];
    }
    
    return days;
  }, [learningPath, isDayUnlocked]);

  // Calculate progress for line graph
  const progressData = useMemo(() => {
    if (!planStarted || allDays.length === 0) {
      const daysCount = Math.max(1, Math.min(totalDays || 1, 30));
      return Array.from({ length: daysCount }, (_, i) => ({
        day: i + 1,
        progress: 0,
      }));
    }
    
    try {
      return allDays.map((day, idx) => ({
        day: day.dayNumber || idx + 1,
        progress: day.isUnlocked ? Math.min(100, ((idx + 1) / allDays.length) * 100) : 0,
      }));
    } catch (e) {
      console.error('Error calculating progress data:', e);
      return Array.from({ length: Math.min(totalDays || 1, 30) }, (_, i) => ({
        day: i + 1,
        progress: 0,
      }));
    }
  }, [allDays, planStarted, totalDays]);

  // Beautiful loading state (initial load or polling while profile/path are being generated)
  if (isLoading || isPolling) {
    const message = LOADING_MESSAGES[loadingMessageIndex];
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary/5 to-violet-500/10 dark:from-slate-950 dark:via-primary/10 dark:to-violet-950/30">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center max-w-md w-full space-y-8"
          >
            <div className="relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                className="w-24 h-24 rounded-full border-4 border-dashed border-primary/40"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-2 w-20 h-20 rounded-full border-2 border-dashed border-violet-500/30"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Target className="w-10 h-10 text-primary animate-pulse" />
              </div>
            </div>
            <div className="text-center space-y-3">
              <motion.p
                key={loadingMessageIndex}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-lg font-medium text-foreground"
              >
                {message}
              </motion.p>
              <div className="flex items-center justify-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                    className="w-2 h-2 rounded-full bg-primary"
                  />
                ))}
              </div>
            </div>
            {isPolling && (
              <p className="text-sm text-muted-foreground text-center max-w-sm">
                Your profile, bio, and learning plan are being created. This usually takes 1–2 minutes.
              </p>
            )}
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  const isStillGeneratingError = error?.message?.includes('still being generated') ?? false;

  if (error && !learningPath && !isStillGeneratingError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex items-center justify-center min-h-[calc(100vh-8rem)]">
          <ErrorState
            error={error}
            onRetry={() => window.location.reload()}
          />
        </main>
        <Footer />
      </div>
    );
  }

  // Page is visible: show "generating" (or "taking longer") when path not ready yet
  if (!learningPath) {
    const takingLonger = Boolean(error && isStillGeneratingError);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary/5 to-violet-500/10 dark:from-slate-950 dark:via-primary/10 dark:to-violet-950/30">
        <Header />
        <main className="flex flex-col items-center justify-center min-h-[calc(100vh-8rem)] px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center max-w-md w-full space-y-6 text-center"
          >
            <div className="relative">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                className="w-20 h-20 rounded-full border-4 border-dashed border-primary/40"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Target className="w-8 h-8 text-primary animate-pulse" />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {takingLonger ? 'Taking a bit longer than usual' : 'Your learning path is being created'}
              </h2>
              <p className="text-muted-foreground mt-2 text-sm">
                {takingLonger
                  ? 'Click below to check again in a minute.'
                  : 'This usually takes 1–2 minutes. You can stay on this page — it will update automatically.'}
              </p>
            </div>
            <Button onClick={() => window.location.reload()} variant="outline" className="rounded-xl">
              Check again
            </Button>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-slate-50 to-primary/5 dark:via-slate-950 dark:to-primary/10">
      <Header />
      <main className="py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container-custom max-w-6xl w-full px-4 sm:px-6 space-y-8">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-start justify-between gap-4"
          >
            <div className="flex-1 space-y-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="mb-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center shadow-lg shadow-primary/25">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary to-violet-600">
                      {learningPath.title}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                      Your personalized career transformation journey
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-0">
                    {learningPath.difficultyLevel}
                  </Badge>
                  <Badge variant="outline" className="bg-violet-500/10 text-violet-600 border-violet-500/20">
                    {learningPath.planType?.replace(/_/g, ' ') || learningPath.pathType?.replace(/_/g, ' ')}
                  </Badge>
                  {totalDays > 0 && (
                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                      <Calendar className="w-3 h-3 mr-1" />
                      {totalDays} Days
                    </Badge>
                  )}
                  {learningPath.estimatedDurationHours && (
                    <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                      <Clock className="w-3 h-3 mr-1" />
                      {learningPath.estimatedDurationHours} Hours
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            {!planStarted && (
              <Button
                size="lg"
                className="bg-gradient-to-r from-primary to-violet-600 hover:shadow-xl hover:shadow-primary/20 text-white rounded-xl px-8 h-12 font-bold"
                onClick={handleStartPlan}
              >
                <Play className="w-5 h-5 mr-2" />
                Start Journey
              </Button>
            )}
          </motion.div>

          {/* User Profile card — show when profile is loaded */}
          {profile && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
            >
              <Card className="border-0 shadow-xl bg-gradient-to-br from-card via-card to-primary/5 overflow-hidden">
                <CardContent className="p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row gap-6 items-start">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-lg shrink-0">
                      {profile.firstName?.[0] ?? profile.email?.[0] ?? userEmail?.[0] ?? '?'}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <h2 className="text-xl sm:text-2xl font-bold text-foreground">
                        {profile.firstName || profile.lastName
                          ? [profile.firstName, profile.lastName].filter(Boolean).join(' ')
                          : userEmail?.split('@')[0] ?? 'Your profile'}
                      </h2>
                      {profile.email && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Mail className="w-4 h-4 shrink-0" />
                          <span className="truncate">{profile.email}</span>
                        </div>
                      )}
                      {(profile as { location?: { city?: string; country?: string } })?.location && (
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                          <MapPin className="w-4 h-4 shrink-0" />
                          <span>
                            {[(profile as { location?: { city?: string; country?: string } }).location?.city, (profile as { location?: { city?: string; country?: string } }).location?.country]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                      {(profile.bio as { summary?: string })?.summary && (
                        <p className="text-sm text-muted-foreground line-clamp-3 pt-1">
                          {(profile.bio as { summary?: string }).summary}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Description Card */}
          {learningPath.description && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md overflow-hidden relative">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                  <BrainCircuit className="w-32 h-32" />
                </div>
                <CardContent className="p-8 relative">
                  <div className="flex items-start gap-4">
                    <div className="w-1 h-full bg-gradient-to-b from-primary to-transparent rounded-full" />
                    <p className="text-lg leading-relaxed text-muted-foreground">
                      {learningPath.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Overview Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            <Card className="border-0 shadow-lg bg-gradient-to-br from-primary/5 to-primary/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Modules</p>
                    <p className="text-3xl font-bold">{totalModules}</p>
                  </div>
                  <BookOpen className="w-8 h-8 text-primary/40" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-gradient-to-br from-violet-500/5 to-violet-500/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Duration</p>
                    <p className="text-3xl font-bold">{totalDays}</p>
                    <p className="text-xs text-muted-foreground mt-1">days</p>
                  </div>
                  <Calendar className="w-8 h-8 text-violet-500/40" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500/5 to-emerald-500/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Skills</p>
                    <p className="text-3xl font-bold">{learningPath.addressingSkillSets?.length || 0}</p>
                  </div>
                  <Target className="w-8 h-8 text-emerald-500/40" />
                </div>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500/5 to-amber-500/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Progress</p>
                    <p className="text-3xl font-bold">{planStarted ? '0%' : '—'}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-amber-500/40" />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Progress Line Graph */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md overflow-hidden">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Learning Progress Timeline
                </CardTitle>
                <CardDescription className="flex items-center justify-between gap-4 flex-wrap">
                  <span>Planned path vs your actual progress — see how far you've come!</span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => document.getElementById('detailed-learning-path')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <BrainCircuit className="w-4 h-4 mr-2" />
                    View detailed structure
                  </Button>
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <div className="space-y-6">
                  {/* Dual Line Graph - Planned vs Completed */}
                  {progressData && progressData.length > 0 ? (
                    <div className="relative h-80 w-full">
                      <svg className="w-full h-full" viewBox={`0 0 ${Math.max(progressData.length * 40, 800)} 240`} preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="progressGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgb(139, 92, 246)" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="rgb(139, 92, 246)" stopOpacity="0.05" />
                          </linearGradient>
                          <linearGradient id="plannedGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="rgb(148, 163, 184)" stopOpacity="0.2" />
                            <stop offset="100%" stopColor="rgb(148, 163, 184)" stopOpacity="0.05" />
                          </linearGradient>
                        </defs>
                        
                        {/* Grid lines */}
                        {[0, 25, 50, 75, 100].map((y) => (
                          <line
                            key={y}
                            x1="0"
                            y1={240 - (y * 2.4)}
                            x2={Math.max(progressData.length * 40, 800)}
                            y2={240 - (y * 2.4)}
                            stroke="currentColor"
                            strokeWidth="0.5"
                            className="text-muted-foreground/20"
                          />
                        ))}
                        
                        {/* Planned path area (full path) */}
                        {progressData.length > 0 && (
                          <path
                            d={`M 0,240 ${progressData.map((_, idx) => {
                              const x = idx * 40 + 20;
                              const plannedProgress = ((idx + 1) / progressData.length) * 100;
                              const y = 240 - (Math.min(100, Math.max(0, plannedProgress)) * 2.4);
                              return `L ${x},${y}`;
                            }).join(' ')} L ${Math.max(progressData.length * 40, 800)},240 Z`}
                            fill="url(#plannedGradient)"
                            opacity="0.5"
                          />
                        )}
                        
                        {/* Planned path line (dashed) */}
                        {progressData.length > 0 && (
                          <polyline
                            points={progressData.map((_, idx) => {
                              const x = idx * 40 + 20;
                              const plannedProgress = ((idx + 1) / progressData.length) * 100;
                              const y = 240 - (Math.min(100, Math.max(0, plannedProgress)) * 2.4);
                              return `${x},${y}`;
                            }).join(' ')}
                            fill="none"
                            stroke="rgb(148, 163, 184)"
                            strokeWidth="2"
                            strokeDasharray="5,5"
                            strokeLinecap="round"
                            className="opacity-60"
                          />
                        )}
                        
                        {/* Completed progress area fill */}
                        {progressData.length > 0 && (
                          <path
                            d={`M 0,240 ${progressData.map((point, idx) => {
                              const x = idx * 40 + 20;
                              const y = 240 - (Math.min(100, Math.max(0, point.progress || 0)) * 2.4);
                              return `L ${x},${y}`;
                            }).join(' ')} L ${Math.max(progressData.length * 40, 800)},240 Z`}
                            fill="url(#progressGradient)"
                          />
                        )}
                        
                        {/* Completed progress line (solid) */}
                        {progressData.length > 0 && (
                          <polyline
                            points={progressData.map((point, idx) => {
                              const x = idx * 40 + 20;
                              const y = 240 - (Math.min(100, Math.max(0, point.progress || 0)) * 2.4);
                              return `${x},${y}`;
                            }).join(' ')}
                            fill="none"
                            stroke="rgb(139, 92, 246)"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="drop-shadow-lg"
                          />
                        )}
                        
                        {/* Data points - Completed */}
                        {progressData.map((point, idx) => {
                          const x = idx * 40 + 20;
                          const y = 240 - (Math.min(100, Math.max(0, point.progress || 0)) * 2.4);
                          const isUnlocked = allDays && allDays[idx]?.isUnlocked;
                          
                          return (
                            <g key={idx}>
                              <circle
                                cx={x}
                                cy={y}
                                r={isUnlocked ? "7" : "5"}
                                fill={isUnlocked ? "rgb(139, 92, 246)" : "rgb(148, 163, 184)"}
                                className="drop-shadow-md"
                              />
                              {isUnlocked && (
                                <circle
                                  cx={x}
                                  cy={y}
                                  r="3"
                                  fill="white"
                                />
                              )}
                            </g>
                          );
                        })}
                        
                        {/* Planned path data points */}
                        {progressData.map((_, idx) => {
                          const x = idx * 40 + 20;
                          const plannedProgress = ((idx + 1) / progressData.length) * 100;
                          const y = 240 - (Math.min(100, Math.max(0, plannedProgress)) * 2.4);
                          const isUnlocked = allDays && allDays[idx]?.isUnlocked;
                          if (!isUnlocked) {
                            return (
                              <circle
                                key={`planned-${idx}`}
                                cx={x}
                                cy={y}
                                r="4"
                                fill="rgb(148, 163, 184)"
                                opacity="0.4"
                              />
                            );
                          }
                          return null;
                        })}
                      </svg>
                      
                      {/* X-axis labels */}
                      {progressData.length > 0 && (
                        <div className="flex justify-between mt-4 text-xs text-muted-foreground">
                          {progressData
                            .filter((_, idx) => {
                              const step = Math.max(1, Math.ceil(progressData.length / 5));
                              return idx % step === 0 || idx === progressData.length - 1;
                            })
                            .map((p, idx) => (
                              <span key={idx}>Day {p.day ?? idx + 1}</span>
                            ))}
                        </div>
                      )}
                      
                      {/* Graph Legend */}
                      <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-0.5 bg-primary" />
                          <span className="text-xs text-muted-foreground">Completed Progress</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-0.5 bg-slate-400 border-dashed border-t-2" />
                          <span className="text-xs text-muted-foreground">Planned Path</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-muted-foreground">
                      <p>No progress data available yet</p>
                    </div>
                  )}
                  
                  {/* Progress stats */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-primary">
                        {allDays ? allDays.filter((d) => d.isUnlocked).length : 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Days Unlocked</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-violet-600">
                        {allDays ? allDays.length : 0}
                      </p>
                      <p className="text-xs text-muted-foreground">Total Days</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">
                        {planStarted && allDays && allDays.length > 0
                          ? Math.round((allDays.filter((d) => d.isUnlocked).length / allDays.length) * 100)
                          : 0}%
                      </p>
                      <p className="text-xs text-muted-foreground">Progress</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Calendar view: Your week at a glance — all days visible, what's coming next */}
          {allDays && allDays.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
              className="space-y-4"
            >
              <Card className="border-0 shadow-xl bg-gradient-to-br from-primary/5 via-card to-violet-500/5 overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Calendar className="w-5 h-5 text-primary" />
                    Your week at a glance
                  </CardTitle>
                  <CardDescription>
                    All {allDays.length} days of your journey — completed, in progress, and what&apos;s coming next
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 sm:p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                    {allDays.map((dayData, idx) => {
                      const isCurrentDay = planStarted && idx === allDays.filter((d) => d.isUnlocked).length - 1;
                      const status = !planStarted
                        ? 'coming_up'
                        : dayData.isUnlocked
                          ? (isCurrentDay ? 'in_progress' : 'completed')
                          : 'coming_up';
                      const label =
                        status === 'completed'
                          ? 'Completed'
                          : status === 'in_progress'
                            ? 'Today'
                            : 'Coming up';
                      const dayLabel = getLearningPathDayDisplayLabel(dayData.day ?? dayData, dayData.dayNumber, 40);
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.02 * idx }}
                          className={`rounded-xl border-2 p-4 transition-all ${
                            status === 'completed'
                              ? 'bg-emerald-500/10 border-emerald-500/30'
                              : status === 'in_progress'
                                ? 'bg-primary/15 border-primary/40 ring-2 ring-primary/20'
                                : 'bg-muted/40 border-border hover:border-primary/30'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <span className="text-xs font-bold text-muted-foreground">Day {dayData.dayNumber}</span>
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${
                                status === 'completed'
                                  ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
                                  : status === 'in_progress'
                                    ? 'bg-primary/20 text-primary'
                                    : 'bg-muted text-muted-foreground'
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-foreground leading-snug line-clamp-2">
                            {dayLabel}
                          </p>
                          {dayData.day?.estimatedDurationMinutes && (
                            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {dayData.day.estimatedDurationMinutes} min
                            </p>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Detailed Learning Path Structure */}
          {learningPath && (
            <motion.div
              id="detailed-learning-path"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.31 }}
            >
              <Card className="border border-border/80 shadow-lg bg-card overflow-hidden">
                <CardHeader className="bg-muted/30 border-b border-border/60 py-5">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <BrainCircuit className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-semibold text-foreground">
                          Learning path structure
                        </CardTitle>
                        <CardDescription className="text-sm mt-0.5">
                          Modules, days & content — click a module for details
                        </CardDescription>
                      </div>
                    </div>
                    {(learningPath.learningModules?.length ?? 0) > 0 && (
                      <Badge variant="secondary" className="bg-primary/10 text-primary border-0 font-medium">
                        {learningPath.learningModules?.length ?? 0} modules
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {(() => {
                    const modules = learningPath.learningModules ?? [];
                    if (modules.length === 0) {
                      return (
                        <div className="py-10 text-center rounded-xl bg-muted/20 border border-dashed border-border">
                          <BrainCircuit className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                          <p className="text-muted-foreground font-medium text-sm">No module structure yet</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Day-based data will appear here once your path is generated.
                          </p>
                        </div>
                      );
                    }
                    const contentStats = {
                      lessons: 0,
                      nudges: 0,
                      tips: 0,
                      tidbits: 0,
                      hacks: 0,
                      assessments: 0,
                    };
                    modules.forEach((m) => {
                      m.dailyContents?.forEach((day) => {
                        const d = day as Record<string, unknown>;
                        const arr = (x: unknown) => (Array.isArray(x) ? x.length : 0);
                        contentStats.lessons += day.lessonIds?.length ?? arr(d.lesson_ids) ?? 0;
                        contentStats.nudges += day.nudgeIds?.length ?? arr(d.nudge_ids) ?? 0;
                        contentStats.tips += day.tipIds?.length ?? arr(d.tip_ids) ?? 0;
                        contentStats.tidbits += day.tidbitIds?.length ?? arr(d.tidbit_ids) ?? 0;
                        contentStats.hacks += day.hackIds?.length ?? arr(d.hack_ids) ?? 0;
                        if (day.assessmentId ?? d.assessment_id) contentStats.assessments += 1;
                      });
                      const mod = m as Record<string, unknown>;
                      if (m.finalAssessmentId ?? mod.final_assessment_id) contentStats.assessments += 1;
                    });
                    const total = Object.values(contentStats).reduce((a, b) => a + b, 0);
                    const chartItems = [
                      { name: 'Lessons', count: contentStats.lessons, color: 'bg-blue-500' },
                      { name: 'Nudges', count: contentStats.nudges, color: 'bg-violet-500' },
                      { name: 'Tips', count: contentStats.tips, color: 'bg-amber-500' },
                      { name: 'Tidbits', count: contentStats.tidbits, color: 'bg-orange-500' },
                      { name: 'Hacks', count: contentStats.hacks, color: 'bg-rose-500' },
                      { name: 'Assessments', count: contentStats.assessments, color: 'bg-indigo-500' },
                    ].filter((x) => x.count > 0);

                    return (
                      <div className="space-y-6">
                        {/* Content distribution bar chart */}
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-primary" />
                            Content distribution
                          </h3>
                          <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                            {chartItems.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-4">No content counts yet</p>
                            ) : (
                              <div className="space-y-3">
                                {chartItems.map((item, idx) => {
                                  const pct = total > 0 ? (item.count / total) * 100 : 0;
                                  return (
                                    <div key={item.name} className="flex items-center gap-3">
                                      <span className="text-xs font-medium text-muted-foreground w-24 shrink-0">{item.name}</span>
                                      <div className="flex-1 h-6 bg-muted rounded-lg overflow-hidden">
                                        <motion.div
                                          initial={{ width: 0 }}
                                          animate={{ width: `${Math.min(100, pct)}%` }}
                                          transition={{ duration: 0.5, delay: idx * 0.05 }}
                                          className={`h-full ${item.color} rounded-lg`}
                                        />
                                      </div>
                                      <span className="text-xs font-semibold tabular-nums w-10 text-right">{item.count}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                          {[
                            { name: 'Lessons', count: contentStats.lessons, icon: BookOpen, cn: 'bg-blue-500/10 border-blue-500/20 text-blue-600' },
                            { name: 'Nudges', count: contentStats.nudges, icon: Zap, cn: 'bg-violet-500/10 border-violet-500/20 text-violet-600' },
                            { name: 'Tips', count: contentStats.tips, icon: Sparkles, cn: 'bg-amber-500/10 border-amber-500/20 text-amber-600' },
                            { name: 'Tidbits', count: contentStats.tidbits, icon: Award, cn: 'bg-orange-500/10 border-orange-500/20 text-orange-600' },
                            { name: 'Hacks', count: contentStats.hacks, icon: Trophy, cn: 'bg-rose-500/10 border-rose-500/20 text-rose-600' },
                            { name: 'Assessments', count: contentStats.assessments, icon: Target, cn: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-600' },
                          ].map((item) => {
                            const Icon = item.icon;
                            const pct = total > 0 ? (item.count / total) * 100 : 0;
                            return (
                              <motion.div
                                key={item.name}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 }}
                                className={`p-3 rounded-xl border ${item.cn} hover:shadow-md transition-shadow`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <Icon className="w-4 h-4 shrink-0" />
                                  <span className="text-xs font-semibold tabular-nums">{item.count}</span>
                                </div>
                                <p className="text-xs font-medium text-muted-foreground mt-1">{item.name}</p>
                                <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${pct}%` }}
                                    transition={{ duration: 0.5, delay: 0.1 }}
                                    className={`h-full rounded-full ${
                                      item.name === 'Lessons' ? 'bg-blue-500' :
                                      item.name === 'Nudges' ? 'bg-violet-500' :
                                      item.name === 'Tips' ? 'bg-amber-500' :
                                      item.name === 'Tidbits' ? 'bg-orange-500' :
                                      item.name === 'Hacks' ? 'bg-rose-500' : 'bg-indigo-500'
                                    }`}
                                  />
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>

                        {/* Module-by-Module — click to expand */}
                        <div className="space-y-4 pt-4 border-t border-border/60">
                          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <Target className="w-4 h-4 text-primary" />
                            Modules — click for day-by-day details
                          </h3>
                          <div className="space-y-6">
                            {modules.map((module, moduleIdx) => {
                              const moduleDays = allDays?.filter((d) => d.moduleIdx === moduleIdx) ?? [];
                              const completedDays = moduleDays.filter((d) => d.isUnlocked).length;
                              const totalModuleDays = moduleDays.length;
                              const isExp = expandedModules.has(moduleIdx);
                              const modContent = {
                                lessons: 0,
                                nudges: 0,
                                tips: 0,
                                tidbits: 0,
                                hacks: 0,
                                assessments: module.finalAssessmentId ? 1 : 0,
                              };
                              module.dailyContents?.forEach((day) => {
                                modContent.lessons += day.lessonIds?.length || 0;
                                modContent.nudges += day.nudgeIds?.length || 0;
                                modContent.tips += day.tipIds?.length || 0;
                                modContent.tidbits += day.tidbitIds?.length || 0;
                                modContent.hacks += day.hackIds?.length || 0;
                                if (day.assessmentId) modContent.assessments += 1;
                              });
                              return (
                                <motion.div
                                  key={moduleIdx}
                                  initial={{ opacity: 0, y: 12 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ delay: moduleIdx * 0.05 }}
                                  className="rounded-xl border border-border overflow-hidden bg-card shadow-sm hover:shadow-md hover:border-primary/30 transition-all"
                                >
                                  <div
                                    className="p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors flex items-center gap-4"
                                    onClick={() => toggleModule(moduleIdx)}
                                  >
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-lg shrink-0">
                                      {moduleIdx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <h4 className="text-base font-semibold text-foreground">Module {moduleIdx + 1}</h4>
                                        {module.totalDays != null && (
                                          <Badge variant="secondary" className="text-xs font-medium">
                                            {module.totalDays} days
                                          </Badge>
                                        )}
                                      </div>
                                      {('description' in module && typeof (module as { description?: string }).description === 'string' && (module as { description: string }).description !== '') && (
                                        <p className="text-sm text-muted-foreground line-clamp-2">{(module as { description: string }).description}</p>
                                      )}
                                      <div className="flex items-center justify-between gap-2 pt-1">
                                        <div className="flex-1 min-w-0">
                                          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                                            <motion.div
                                              initial={{ width: 0 }}
                                              animate={{ width: `${totalModuleDays > 0 ? (completedDays / totalModuleDays) * 100 : 0}%` }}
                                              transition={{ duration: 0.5, delay: moduleIdx * 0.05 }}
                                              className="h-full bg-primary rounded-full"
                                            />
                                          </div>
                                        </div>
                                        <span className="text-xs font-medium tabular-nums text-muted-foreground shrink-0">
                                          {completedDays}/{totalModuleDays}
                                        </span>
                                      </div>
                                    </div>
                                    <ChevronRight className={`w-5 h-5 text-muted-foreground shrink-0 transition-transform ${isExp ? 'rotate-90' : ''}`} />
                                  </div>
                                  <AnimatePresence>
                                    {isExp && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        className="border-t border-border/60 bg-muted/20"
                                      >
                                        <div className="p-6 space-y-6">
                                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                            {[
                                              { name: 'Lessons', count: modContent.lessons, icon: BookOpen, color: 'blue' },
                                              { name: 'Nudges', count: modContent.nudges, icon: Zap, color: 'purple' },
                                              { name: 'Tips', count: modContent.tips, icon: Sparkles, color: 'yellow' },
                                              { name: 'Tidbits', count: modContent.tidbits, icon: Award, color: 'orange' },
                                              { name: 'Hacks', count: modContent.hacks, icon: Trophy, color: 'red' },
                                              { name: 'Assessments', count: modContent.assessments, icon: Target, color: 'indigo' },
                                            ].map((item) => {
                                              const Icon = item.icon;
                                              return (
                                                <div key={item.name} className="p-3 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-center">
                                                  <Icon className={`w-5 h-5 mx-auto mb-2 ${
                                                    item.color === 'blue' ? 'text-blue-600' :
                                                    item.color === 'purple' ? 'text-purple-600' :
                                                    item.color === 'yellow' ? 'text-yellow-600' :
                                                    item.color === 'orange' ? 'text-orange-600' :
                                                    item.color === 'red' ? 'text-red-600' : 'text-indigo-600'
                                                  }`} />
                                                  <p className="text-xl font-bold">{item.count}</p>
                                                  <p className="text-xs text-muted-foreground">{item.name}</p>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          {module.dailyContents && module.dailyContents.length > 0 && (
                                            <div className="space-y-4">
                                              <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                                <Calendar className="w-4 h-4 text-primary" />
                                                Day-by-day schedule
                                              </h5>
                                              <div className="grid gap-3">
                                                {module.dailyContents.map((day, dayIdx) => {
                                                  const d = allDays?.find((x) => x.moduleIdx === moduleIdx && x.dayIdx === dayIdx);
                                                  const un = d?.isUnlocked ?? false;
                                                  return (
                                                    <motion.div
                                                      key={dayIdx}
                                                      initial={{ opacity: 0, x: -10 }}
                                                      animate={{ opacity: 1, x: 0 }}
                                                      transition={{ delay: dayIdx * 0.05 }}
                                                      className={`p-4 rounded-xl border-2 ${un ? 'bg-primary/5 border-primary/20' : 'bg-slate-100/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'}`}
                                                    >
                                                      <div className="flex items-start justify-between gap-4">
                                                        <div className="flex items-start gap-3 flex-1">
                                                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold flex-shrink-0 ${un ? 'bg-primary text-white' : 'bg-slate-300 dark:bg-slate-700 text-slate-600'}`}>
                                                            {day.dayNumber ?? dayIdx + 1}
                                                          </div>
                                                          <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                              <h6 className="font-semibold">{getLearningPathDayDisplayLabel(day, day.dayNumber ?? dayIdx + 1)}</h6>
                                                              {un ? <Unlock className="w-4 h-4 text-emerald-500" /> : <Lock className="w-4 h-4 text-muted-foreground" />}
                                                            </div>
                                                            {day.description != null && day.description !== '' && (
                                                              <p className="text-sm text-muted-foreground mb-2">{day.description}</p>
                                                            )}
                                                            <div className="flex flex-wrap gap-2">
                                                              {day.lessonIds && day.lessonIds.length > 0 && (
                                                                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/20">
                                                                  <BookOpen className="w-3 h-3 mr-1" />
                                                                  {day.lessonIds.length} Lessons
                                                                </Badge>
                                                              )}
                                                              {day.nudgeIds && day.nudgeIds.length > 0 && (
                                                                <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/20">
                                                                  <Zap className="w-3 h-3 mr-1" />
                                                                  {day.nudgeIds.length} Nudges
                                                                </Badge>
                                                              )}
                                                              {day.tipIds && day.tipIds.length > 0 && (
                                                                <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                                                                  <Sparkles className="w-3 h-3 mr-1" />
                                                                  {day.tipIds.length} Tips
                                                                </Badge>
                                                              )}
                                                              {day.estimatedDurationMinutes != null && (
                                                                <Badge variant="outline" className="text-xs">
                                                                  <Clock className="w-3 h-3 mr-1" />
                                                                  {day.estimatedDurationMinutes} min
                                                                </Badge>
                                                              )}
                                                            </div>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </motion.div>
                                                  );
                                                })}
                                              </div>
                                            </div>
                                          )}
                                          {module.finalAssessmentId && (
                                            <div className="p-4 rounded-xl bg-indigo-500/10 border-2 border-indigo-500/20">
                                              <div className="flex items-center gap-3">
                                                <Target className="w-6 h-6 text-indigo-600" />
                                                <div>
                                                  <p className="font-semibold">Final Assessment</p>
                                                  <p className="text-sm text-muted-foreground">
                                                    Complete this assessment to finish Module {moduleIdx + 1}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Complete Learning Path Visualization - Day by Day Graph */}
          {allDays && allDays.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.32 }}
            >
              <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md overflow-hidden">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Complete Learning Path Overview
                  </CardTitle>
                  <CardDescription>
                    Day-by-day breakdown of your entire learning journey - Planned vs Completed
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-6">
                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-6 pb-4 border-b">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-primary shadow-md" />
                        <span className="text-sm font-medium">Completed/Unlocked</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-slate-300 dark:bg-slate-700 border-2 border-slate-400" />
                        <span className="text-sm font-medium">Planned/Locked</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-gradient-to-r from-primary to-violet-600" />
                        <span className="text-sm font-medium">Current Day</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded border-2 border-amber-500 bg-amber-500/20" />
                        <span className="text-sm font-medium">Milestone</span>
                      </div>
                    </div>

                    {/* Module Breakdown Chart */}
                    {learningPath.learningModules && learningPath.learningModules.length > 0 && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold">Module Progress</h3>
                        <div className="space-y-3">
                          {learningPath.learningModules.map((_mod, moduleIdx) => {
                            const moduleDays = allDays.filter((d) => d.moduleIdx === moduleIdx);
                            const completedDays = moduleDays.filter((d) => d.isUnlocked).length;
                            const totalModuleDays = moduleDays.length;
                            const progressPercent = totalModuleDays > 0 ? (completedDays / totalModuleDays) * 100 : 0;

                            return (
                              <div key={moduleIdx} className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center text-white font-bold shadow-lg">
                                      {moduleIdx + 1}
                                    </div>
                                    <div>
                                      <p className="font-semibold">Module {moduleIdx + 1}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {completedDays} / {totalModuleDays} days completed
                                      </p>
                                    </div>
                                  </div>
                                  <Badge variant="outline" className="text-sm">
                                    {Math.round(progressPercent)}%
                                  </Badge>
                                </div>
                                <div className="relative h-8 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                                  <motion.div
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progressPercent}%` }}
                                    transition={{ duration: 0.8, delay: moduleIdx * 0.1 }}
                                    className="h-full bg-gradient-to-r from-primary to-violet-600 rounded-full"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-foreground">
                                    {completedDays} / {totalModuleDays}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Day-by-Day Gantt Chart */}
                    <div className="space-y-4 pt-4 border-t">
                      <h3 className="text-lg font-semibold">Day-by-Day Path Visualization</h3>
                      <div className="relative overflow-x-auto">
                        <div className="min-w-full space-y-2">
                          {/* Day bars */}
                          {allDays.map((dayData, idx) => {
                            const isCurrentDay = idx === allDays.filter((d) => d.isUnlocked).length - 1 && planStarted;
                            const isMilestone = dayData.day?.isMilestone || false;
                            
                            return (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.02 }}
                                className="flex items-center gap-4 group"
                              >
                                {/* Day number */}
                                <div className="w-16 flex-shrink-0 text-right">
                                  <span className="text-sm font-bold text-muted-foreground">
                                    Day {dayData.dayNumber}
                                  </span>
                                </div>

                                {/* Status indicator */}
                                <div className="w-4 h-4 flex-shrink-0">
                                  {dayData.isUnlocked ? (
                                    <div className="w-4 h-4 rounded-full bg-primary shadow-lg shadow-primary/50 flex items-center justify-center">
                                      <CheckCircle2 className="w-3 h-3 text-white" />
                                    </div>
                                  ) : (
                                    <div className="w-4 h-4 rounded-full bg-slate-300 dark:bg-slate-700 border-2 border-slate-400 dark:border-slate-600" />
                                  )}
                                </div>

                                {/* Day bar */}
                                <div className="flex-1 relative">
                                  <div
                                    className={`h-12 rounded-lg flex items-center px-4 transition-all ${
                                      dayData.isUnlocked
                                        ? isCurrentDay
                                          ? 'bg-gradient-to-r from-primary to-violet-600 shadow-lg shadow-primary/30'
                                          : 'bg-primary/80 hover:bg-primary'
                                        : 'bg-slate-200 dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-700'
                                    } ${isMilestone ? 'ring-2 ring-amber-500 ring-offset-2' : ''}`}
                                  >
                                    <div className="flex items-center justify-between w-full">
                                      <div className="flex items-center gap-3 flex-1 min-w-0">
                                        <div className="flex-shrink-0">
                                          {isMilestone && (
                                            <Trophy className="w-5 h-5 text-amber-500" />
                                          )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`font-semibold truncate ${
                                            dayData.isUnlocked ? 'text-white' : 'text-slate-600 dark:text-slate-400'
                                          }`}>
                                            {getLearningPathDayDisplayLabel(dayData.day ?? dayData, dayData.dayNumber)}
                                          </p>
                                          {dayData.description && (
                                            <p className={`text-xs truncate ${
                                              dayData.isUnlocked ? 'text-white/80' : 'text-slate-500 dark:text-slate-500'
                                            }`}>
                                              {dayData.description}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                                        {dayData.day?.estimatedDurationMinutes && (
                                          <Badge
                                            variant="outline"
                                            className={`text-xs ${
                                              dayData.isUnlocked
                                                ? 'bg-white/20 text-white border-white/30'
                                                : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                            }`}
                                          >
                                            <Clock className="w-3 h-3 mr-1" />
                                            {dayData.day.estimatedDurationMinutes} min
                                          </Badge>
                                        )}
                                        {dayData.isUnlocked ? (
                                          <Unlock className="w-4 h-4 text-white" />
                                        ) : (
                                          <Lock className="w-4 h-4 text-slate-500" />
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Content summary tooltip */}
                                  {dayData.day && (
                                    <div className="absolute left-0 top-full mt-2 w-full bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 p-3 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                      <div className="flex flex-wrap gap-2">
                                        {dayData.day.lessonIds?.length ? (
                                          <Badge variant="outline" className="text-xs">
                                            <BookOpen className="w-3 h-3 mr-1" />
                                            {dayData.day.lessonIds?.length ?? 0} Lessons
                                          </Badge>
                                        ) : null}
                                        {dayData.day.nudgeIds?.length ? (
                                          <Badge variant="outline" className="text-xs">
                                            <Zap className="w-3 h-3 mr-1" />
                                            {dayData.day.nudgeIds?.length ?? 0} Nudges
                                          </Badge>
                                        ) : null}
                                        {dayData.day.tipIds?.length ? (
                                          <Badge variant="outline" className="text-xs">
                                            <Sparkles className="w-3 h-3 mr-1" />
                                            {dayData.day.tipIds?.length ?? 0} Tips
                                          </Badge>
                                        ) : null}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                      <div className="text-center p-4 rounded-xl bg-primary/5 border border-primary/20">
                        <p className="text-2xl font-bold text-primary">{allDays.length}</p>
                        <p className="text-xs text-muted-foreground mt-1">Total Days</p>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20">
                        <p className="text-2xl font-bold text-emerald-600">
                          {allDays.filter((d) => d.isUnlocked).length}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Completed</p>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-slate-500/5 border border-slate-500/20">
                        <p className="text-2xl font-bold text-slate-600">
                          {allDays.filter((d) => !d.isUnlocked).length}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Remaining</p>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-violet-500/5 border border-violet-500/20">
                        <p className="text-2xl font-bold text-violet-600">
                          {allDays.length > 0
                            ? Math.round((allDays.filter((d) => d.isUnlocked).length / allDays.length) * 100)
                            : 0}%
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">Progress</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Day-by-Day Timeline View */}
          {allDays && allDays.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Daily Journey Timeline
                  </CardTitle>
                  <CardDescription>
                    Your complete learning path structure - unlock days as you progress
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-violet-500 to-transparent" />
                    
                    <div className="space-y-6">
                      {allDays
                        .filter((dayData) => dayData && dayData.day)
                        .map((dayData, idx) => {
                        
                        const dayKey = `module-${dayData.moduleIdx}-day-${dayData.dayIdx}`;
                        const isExpanded = expandedDays.has(dayKey);
                        const isUnlocked = dayData.isUnlocked || false;
                        const day = dayData.day || {};
                        const hasContent = 
                          day.lessonIds?.length ||
                          day.nudgeIds?.length ||
                          day.tipIds?.length ||
                          day.tidbitIds?.length ||
                          day.hackIds?.length ||
                          day.assessmentId;

                        return (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.03 }}
                            className="relative pl-20"
                          >
                            {/* Timeline dot */}
                            <div className={`absolute left-6 top-6 w-4 h-4 rounded-full border-4 ${
                              isUnlocked
                                ? 'bg-primary border-primary shadow-lg shadow-primary/50'
                                : 'bg-slate-300 dark:bg-slate-700 border-slate-400 dark:border-slate-600'
                            }`}>
                              {isUnlocked && (
                                <CheckCircle2 className="w-4 h-4 text-white absolute -top-0.5 -left-0.5" />
                              )}
                            </div>

                            {/* Day Card */}
                            <Card
                              className={`border-2 transition-all cursor-pointer hover:shadow-lg ${
                                isUnlocked
                                  ? 'border-primary/30 bg-primary/5 hover:border-primary/50'
                                  : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30'
                              }`}
                              onClick={() => {
                                if (isUnlocked) {
                                  toggleDay(dayData.moduleIdx, dayData.dayIdx);
                                }
                              }}
                            >
                              <CardContent className="p-5">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 space-y-2">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                                        isUnlocked
                                          ? 'bg-primary text-white shadow-lg'
                                          : 'bg-slate-200 dark:bg-slate-800 text-slate-500'
                                      }`}>
                                        {dayData.dayNumber}
                                      </div>
                                      <div className="flex-1">
                                        <h4 className="font-semibold text-lg flex items-center gap-2">
                                          {getLearningPathDayDisplayLabel(dayData?.day ?? dayData, dayData?.dayNumber ?? idx + 1)}
                                          {!isUnlocked && (
                                            <Lock className="w-4 h-4 text-muted-foreground" />
                                          )}
                                          {isUnlocked && (
                                            <Unlock className="w-4 h-4 text-emerald-500" />
                                          )}
                                        </h4>
                                        {dayData?.description && (
                                          <p className={`text-sm mt-1 ${
                                            isUnlocked ? 'text-muted-foreground' : 'text-muted-foreground blur-sm select-none'
                                          }`}>
                                            {dayData.description}
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    {/* Content summary - always visible but blurred if locked */}
                                    {hasContent && (
                                      <div className={`flex flex-wrap gap-2 pt-2 ${
                                        !isUnlocked ? 'blur-sm select-none' : ''
                                      }`}>
                                        {day?.lessonIds && day.lessonIds.length > 0 && (
                                          <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                                            <BookOpen className="w-3 h-3 mr-1" />
                                            {day.lessonIds.length} Lesson{day.lessonIds.length !== 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                        {day?.nudgeIds && day.nudgeIds.length > 0 && (
                                          <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/20">
                                            <Zap className="w-3 h-3 mr-1" />
                                            {day.nudgeIds.length} Nudge{day.nudgeIds.length !== 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                        {day?.tipIds && day.tipIds.length > 0 && (
                                          <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                                            <Sparkles className="w-3 h-3 mr-1" />
                                            {day.tipIds.length} Tip{day.tipIds.length !== 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                        {day?.estimatedDurationMinutes && (
                                          <Badge variant="outline" className="bg-slate-500/10 text-slate-600 border-slate-500/20">
                                            <Clock className="w-3 h-3 mr-1" />
                                            {day.estimatedDurationMinutes} min
                                          </Badge>
                                        )}
                                      </div>
                                    )}

                                    {/* Locked message */}
                                    {!isUnlocked && (
                                      <div className="pt-2 flex items-center gap-2 text-sm text-muted-foreground">
                                        <Lock className="w-4 h-4" />
                                        <span>Complete previous days to unlock this content</span>
                                      </div>
                                    )}
                                  </div>

                                  {isUnlocked && (
                                    <ChevronRight
                                      className={`w-5 h-5 text-muted-foreground transition-transform shrink-0 ${
                                        isExpanded ? 'rotate-90' : ''
                                      }`}
                                    />
                                  )}
                                </div>

                                {/* Expanded content - only show if unlocked and expanded */}
                                {isUnlocked && isExpanded && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="pt-4 mt-4 border-t space-y-4"
                                  >
                                    {/* Learning Objectives */}
                                    {day?.learningObjectives && day.learningObjectives.length > 0 && (
                                      <div>
                                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                          Learning Objectives
                                        </p>
                                        <ul className="space-y-2">
                                          {day.learningObjectives.map((obj: string, objIdx: number) => (
                                            <li key={objIdx} className="flex items-start gap-2 text-sm">
                                              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                                              <span>{obj}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {/* Detailed Content */}
                                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                      {day?.lessonIds?.map((lessonId: string, lessonIdx: number) => (
                                        <div key={lessonIdx} className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                          <div className="flex items-center gap-2 mb-1">
                                            <BookOpen className="w-4 h-4 text-blue-600" />
                                            <span className="text-xs font-semibold text-blue-600">Lesson {lessonIdx + 1}</span>
                                          </div>
                                          <p className="text-xs text-muted-foreground font-mono truncate">{lessonId}</p>
                                        </div>
                                      ))}
                                      {day?.nudgeIds?.map((nudgeId: string, nudgeIdx: number) => (
                                        <div key={nudgeIdx} className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                                          <div className="flex items-center gap-2 mb-1">
                                            <Zap className="w-4 h-4 text-purple-600" />
                                            <span className="text-xs font-semibold text-purple-600">Nudge {nudgeIdx + 1}</span>
                                          </div>
                                          <p className="text-xs text-muted-foreground font-mono truncate">{nudgeId}</p>
                                        </div>
                                      ))}
                                      {day?.tipIds?.map((tipId: string, tipIdx: number) => (
                                        <div key={tipIdx} className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                                          <div className="flex items-center gap-2 mb-1">
                                            <Sparkles className="w-4 h-4 text-yellow-600" />
                                            <span className="text-xs font-semibold text-yellow-600">Tip {tipIdx + 1}</span>
                                          </div>
                                          <p className="text-xs text-muted-foreground font-mono truncate">{tipId}</p>
                                        </div>
                                      ))}
                                    </div>
                                  </motion.div>
                                )}
                              </CardContent>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Skills Addressed */}
          {learningPath.addressingSkillSets && learningPath.addressingSkillSets.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    Skills You'll Master
                  </CardTitle>
                  <CardDescription>
                    Core competencies addressed in this learning path
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {learningPath.addressingSkillSets.map((skill, idx) => (
                      <Badge
                        key={idx}
                        variant="secondary"
                        className="px-4 py-2 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/10 text-sm font-medium"
                      >
                        {skill.skillName || skill.skillId}
                        {skill.proficiencyLevel && (
                          <span className="ml-2 text-xs opacity-70">
                            ({skill.proficiencyLevel})
                          </span>
                        )}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Learning Modules */}
          {learningPath.learningModules && learningPath.learningModules.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-3xl font-bold">Learning Modules</h2>
                <Badge variant="outline" className="text-sm">
                  {totalModules} Modules
                </Badge>
              </div>

              <div className="space-y-4">
                {learningPath.learningModules.map((module, moduleIdx) => {
                  const isExpanded = expandedModules.has(moduleIdx);
                  const isLocked = !planStarted;
                  const dailyContents = module.dailyContents || [];

                  return (
                    <motion.div
                      key={moduleIdx}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + moduleIdx * 0.1 }}
                    >
                      <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md overflow-hidden hover:shadow-2xl transition-shadow">
                        <CardHeader
                          className="cursor-pointer"
                          onClick={() => toggleModule(moduleIdx)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 space-y-2">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-violet-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                  {moduleIdx + 1}
                                </div>
                                <div className="flex-1">
                                  <CardTitle className="text-2xl flex items-center gap-2">
                                    Module {moduleIdx + 1}
                                    {isLocked && (
                                      <Lock className="w-5 h-5 text-muted-foreground" />
                                    )}
                                    {!isLocked && (
                                      <Unlock className="w-5 h-5 text-emerald-500" />
                                    )}
                                  </CardTitle>
                                  {module.totalDays && (
                                    <CardDescription className="mt-1">
                                      {module.totalDays} day journey
                                    </CardDescription>
                                  )}
                                </div>
                              </div>
                            </div>
                            <ChevronRight
                              className={`w-5 h-5 text-muted-foreground transition-transform ${
                                isExpanded ? 'rotate-90' : ''
                              }`}
                            />
                          </div>
                        </CardHeader>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <CardContent className="pt-0 space-y-6">
                                {/* Daily Contents Preview */}
                                {dailyContents.length > 0 && (
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                      <h3 className="text-lg font-semibold flex items-center gap-2">
                                        <Calendar className="w-5 h-5 text-primary" />
                                        Daily Schedule
                                      </h3>
                                      <Badge variant="outline">
                                        {dailyContents.length} Days
                                      </Badge>
                                    </div>

                                    <div className="grid gap-3">
                                      {dailyContents.map((day, dayIdx) => {
                                        const dayKey = `module-${moduleIdx}-day-${dayIdx}`;
                                        const dayUnlocked = isDayUnlocked(moduleIdx, dayIdx);
                                        const isDayExpanded = expandedDays.has(dayKey);
                                        const hasContent =
                                          day.lessonIds?.length ||
                                          day.nudgeIds?.length ||
                                          day.tipIds?.length ||
                                          day.tidbitIds?.length ||
                                          day.hackIds?.length ||
                                          day.assessmentId;

                                        return (
                                          <motion.div
                                            key={dayIdx}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: dayIdx * 0.05 }}
                                            className={`relative rounded-xl border-2 p-5 transition-all cursor-pointer ${
                                              dayUnlocked
                                                ? 'border-primary/20 bg-primary/5 hover:border-primary/40'
                                                : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30'
                                            }`}
                                            onClick={() => {
                                              if (dayUnlocked) {
                                                toggleDay(moduleIdx, dayIdx);
                                              }
                                            }}
                                          >
                                            {/* Lock Overlay */}
                                            {!dayUnlocked && (
                                              <div className="absolute inset-0 bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
                                                <div className="text-center space-y-2">
                                                  <Lock className="w-8 h-8 text-muted-foreground mx-auto" />
                                                  <p className="text-sm font-medium text-muted-foreground">
                                                    {dayIdx === 0 && planStarted
                                                      ? 'Click "Start Journey" to unlock'
                                                      : 'Complete previous days to unlock'}
                                                  </p>
                                                </div>
                                              </div>
                                            )}

                                            <div className="relative space-y-3">
                                              {/* Day Header */}
                                              <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                  <div
                                                    className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                                                      dayUnlocked
                                                        ? 'bg-primary text-white shadow-lg'
                                                        : 'bg-slate-200 dark:bg-slate-800 text-slate-500'
                                                    }`}
                                                  >
                                                    {day.dayNumber || dayIdx + 1}
                                                  </div>
                                                  <div className="flex-1">
                                                    <h4 className="font-semibold text-lg flex items-center gap-2">
                                                      {getLearningPathDayDisplayLabel(day, day.dayNumber ?? dayIdx + 1)}
                                                      {!dayUnlocked && (
                                                        <Lock className="w-4 h-4 text-muted-foreground" />
                                                      )}
                                                      {dayUnlocked && (
                                                        <Unlock className="w-4 h-4 text-emerald-500" />
                                                      )}
                                                    </h4>
                                                    {day.description && (
                                                      <p
                                                        className={`text-sm mt-1 ${
                                                          dayUnlocked
                                                            ? 'text-muted-foreground'
                                                            : 'text-muted-foreground blur-sm select-none'
                                                        }`}
                                                      >
                                                        {day.description}
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  {day.estimatedDurationMinutes && (
                                                    <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                                                      <Clock className="w-3 h-3 mr-1" />
                                                      {day.estimatedDurationMinutes} min
                                                    </Badge>
                                                  )}
                                                  {dayUnlocked && (
                                                    <ChevronRight
                                                      className={`w-5 h-5 text-muted-foreground transition-transform ${
                                                        isDayExpanded ? 'rotate-90' : ''
                                                      }`}
                                                    />
                                                  )}
                                                </div>
                                              </div>

                                              {/* Expanded Content - Only show if unlocked and expanded */}
                                              {dayUnlocked && isDayExpanded && (
                                                <motion.div
                                                  initial={{ height: 0, opacity: 0 }}
                                                  animate={{ height: 'auto', opacity: 1 }}
                                                  exit={{ height: 0, opacity: 0 }}
                                                  className="pt-4 mt-4 border-t space-y-4"
                                                >
                                                  {/* Learning Objectives */}
                                                  {day.learningObjectives && day.learningObjectives.length > 0 && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                                        Learning Objectives
                                                      </p>
                                                      <ul className="space-y-2">
                                                        {day.learningObjectives.map((obj: string, objIdx: number) => (
                                                          <li
                                                            key={objIdx}
                                                            className="flex items-start gap-2 text-sm"
                                                          >
                                                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                                                            <span>{obj}</span>
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    </div>
                                                  )}

                                                  {/* Detailed Lesson/Nudge/Tip Content */}
                                                  {hasContent && (
                                                    <div className="space-y-4">
                                                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                                        Detailed Content
                                                      </p>
                                                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                                        {day.lessonIds?.map((lessonId: string, lessonIdx: number) => (
                                                          <div key={lessonIdx} className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                                                            <div className="flex items-center gap-2 mb-1">
                                                              <BookOpen className="w-4 h-4 text-blue-600" />
                                                              <span className="text-xs font-semibold text-blue-600">Lesson {lessonIdx + 1}</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground font-mono truncate">{lessonId}</p>
                                                          </div>
                                                        ))}
                                                        {day.nudgeIds?.map((nudgeId: string, nudgeIdx: number) => (
                                                          <div key={nudgeIdx} className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                                                            <div className="flex items-center gap-2 mb-1">
                                                              <Zap className="w-4 h-4 text-purple-600" />
                                                              <span className="text-xs font-semibold text-purple-600">Nudge {nudgeIdx + 1}</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground font-mono truncate">{nudgeId}</p>
                                                          </div>
                                                        ))}
                                                        {day.tipIds?.map((tipId: string, tipIdx: number) => (
                                                          <div key={tipIdx} className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                                                            <div className="flex items-center gap-2 mb-1">
                                                              <Sparkles className="w-4 h-4 text-yellow-600" />
                                                              <span className="text-xs font-semibold text-yellow-600">Tip {tipIdx + 1}</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground font-mono truncate">{tipId}</p>
                                                          </div>
                                                        ))}
                                                        {day.tidbitIds?.map((tidbitId: string, tidbitIdx: number) => (
                                                          <div key={tidbitIdx} className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                                                            <div className="flex items-center gap-2 mb-1">
                                                              <Award className="w-4 h-4 text-orange-600" />
                                                              <span className="text-xs font-semibold text-orange-600">Tidbit {tidbitIdx + 1}</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground font-mono truncate">{tidbitId}</p>
                                                          </div>
                                                        ))}
                                                        {day.hackIds?.map((hackId: string, hackIdx: number) => (
                                                          <div key={hackIdx} className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                                                            <div className="flex items-center gap-2 mb-1">
                                                              <Trophy className="w-4 h-4 text-red-600" />
                                                              <span className="text-xs font-semibold text-red-600">Hack {hackIdx + 1}</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground font-mono truncate">{hackId}</p>
                                                          </div>
                                                        ))}
                                                        {day.assessmentId && (
                                                          <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                                                            <div className="flex items-center gap-2 mb-1">
                                                              <Target className="w-4 h-4 text-indigo-600" />
                                                              <span className="text-xs font-semibold text-indigo-600">Assessment</span>
                                                            </div>
                                                            <p className="text-xs text-muted-foreground font-mono truncate">{day.assessmentId}</p>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                                </motion.div>
                                              )}

                                              {/* Content Summary - Always visible but blurred if locked */}
                                              {hasContent && !isDayExpanded && (
                                                <div className="pt-3 border-t">
                                                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                                                    Today's Content Preview
                                                  </p>
                                                  <div
                                                    className={`flex flex-wrap gap-2 ${
                                                      !dayUnlocked ? 'blur-sm select-none' : ''
                                                    }`}
                                                  >
                                                    {day.lessonIds && day.lessonIds.length > 0 && (
                                                      <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                                                        <BookOpen className="w-3 h-3 mr-1" />
                                                        {day.lessonIds.length} Lesson{day.lessonIds.length !== 1 ? 's' : ''}
                                                      </Badge>
                                                    )}
                                                    {day.nudgeIds && day.nudgeIds.length > 0 && (
                                                      <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/20">
                                                        <Zap className="w-3 h-3 mr-1" />
                                                        {day.nudgeIds.length} Nudge{day.nudgeIds.length !== 1 ? 's' : ''}
                                                      </Badge>
                                                    )}
                                                    {day.tipIds && day.tipIds.length > 0 && (
                                                      <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                                                        <Sparkles className="w-3 h-3 mr-1" />
                                                        {day.tipIds.length} Tip{day.tipIds.length !== 1 ? 's' : ''}
                                                      </Badge>
                                                    )}
                                                    {day.tidbitIds && day.tidbitIds.length > 0 && (
                                                      <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20">
                                                        <Award className="w-3 h-3 mr-1" />
                                                        {day.tidbitIds.length} Tidbit{day.tidbitIds.length !== 1 ? 's' : ''}
                                                      </Badge>
                                                    )}
                                                    {day.hackIds && day.hackIds.length > 0 && (
                                                      <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20">
                                                        <Trophy className="w-3 h-3 mr-1" />
                                                        {day.hackIds.length} Hack{day.hackIds.length !== 1 ? 's' : ''}
                                                      </Badge>
                                                    )}
                                                    {day.assessmentId && (
                                                      <Badge variant="outline" className="bg-indigo-500/10 text-indigo-600 border-indigo-500/20">
                                                        <Target className="w-3 h-3 mr-1" />
                                                        Assessment
                                                      </Badge>
                                                    )}
                                                  </div>
                                                  {dayUnlocked && (
                                                    <p className="text-xs text-muted-foreground mt-2 italic">
                                                      Click to expand and view detailed content
                                                    </p>
                                                  )}
                                                  {!dayUnlocked && (
                                                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                                                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                        <Lock className="w-4 h-4" />
                                                        <span>This day is locked</span>
                                                      </div>
                                                      <Button
                                                        variant="outline"
                                                        size="sm"
                                                        disabled
                                                        className="text-xs h-7"
                                                      >
                                                        <Lock className="w-3 h-3 mr-1" />
                                                        Locked
                                                      </Button>
                                                    </div>
                                                  )}
                                                </div>
                                              )}

                                              {/* Milestone Badge */}
                                              {day.isMilestone && (
                                                <div className="pt-2">
                                                  <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white border-0">
                                                    <Trophy className="w-3 h-3 mr-1" />
                                                    Milestone Day
                                                  </Badge>
                                                </div>
                                              )}
                                            </div>
                                          </motion.div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Final Assessment */}
                                {module.finalAssessmentId && (
                                  <div className="pt-4 border-t">
                                    <div className="flex items-center gap-3 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                                      <Target className="w-6 h-6 text-indigo-600" />
                                      <div className="flex-1">
                                        <p className="font-semibold">Final Assessment</p>
                                        <p className="text-sm text-muted-foreground">
                                          Complete this assessment to finish the module
                                        </p>
                                      </div>
                                      {isLocked && <Lock className="w-5 h-5 text-muted-foreground" />}
                                    </div>
                                  </div>
                                )}

                                {/* Final Reflection Prompts */}
                                {module.finalReflectionPrompts &&
                                  module.finalReflectionPrompts.length > 0 && (
                                    <div className="pt-4 border-t space-y-3">
                                      <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                                        Final Reflection Prompts
                                      </p>
                                      <div
                                        className={`space-y-2 ${
                                          isLocked ? 'blur-sm select-none' : ''
                                        }`}
                                      >
                                        {module.finalReflectionPrompts.map(
                                          (prompt: string, promptIdx: number) => (
                                            <div
                                              key={promptIdx}
                                              className="flex items-start gap-3 p-3 rounded-lg bg-purple-500/5 border border-purple-500/10"
                                            >
                                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 text-purple-600 flex items-center justify-center text-xs font-bold">
                                                {promptIdx + 1}
                                              </span>
                                              <p className="text-sm">{prompt}</p>
                                            </div>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </CardContent>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Pain Points Addressed */}
          {learningPath.painPoints && learningPath.painPoints.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Card className="border-0 shadow-xl bg-card/50 backdrop-blur-md">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-orange-500" />
                    Pain Points Addressed
                  </CardTitle>
                  <CardDescription>
                    Challenges this path will help you overcome
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2">
                    {learningPath.painPoints.map((painPoint, idx) => (
                      <div
                        key={idx}
                        className="p-4 rounded-xl bg-orange-500/5 border border-orange-500/20"
                      >
                        <p className="text-sm font-medium">{painPoint.painPointDescription}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Action Footer */}
          {/* AI Career Coach CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
          >
            <Card className="border-0 shadow-2xl overflow-hidden bg-gradient-to-br from-violet-600 via-primary to-violet-700 text-white">
              <CardContent className="p-8 sm:p-10">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-sm">
                      <MessageCircle className="w-7 h-7" />
                    </div>
                    <div className="space-y-2">
                      <h3 className="text-xl sm:text-2xl font-bold">Talk to your AI Career Coach</h3>
                      <p className="text-white/90 text-sm sm:text-base max-w-xl">
                        Get personalized guidance, practice conversations, and unlock your next steps. Your coach knows your profile and learning path.
                      </p>
                    </div>
                  </div>
                  <Button
                    size="lg"
                    className="bg-white text-primary hover:bg-white/95 font-bold rounded-xl px-8 h-12 shrink-0 shadow-lg"
                    onClick={() => navigate('/coach')}
                  >
                    Open AI Coach
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-6 border-t"
          >
            <div className="space-y-1">
              <p className="font-semibold text-foreground">Ready to transform your career?</p>
              <p className="text-sm text-muted-foreground">
                {planStarted
                  ? 'Continue your journey and unlock new modules as you progress.'
                  : 'Start your learning path to begin unlocking daily content.'}
              </p>
            </div>
            <div className="flex gap-3">
              {profile && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/profiles/${encodeURIComponent(userEmail || '')}`)}
                  className="rounded-xl"
                >
                  <User className="w-4 h-4 mr-2" />
                  View Profile
                </Button>
              )}
              {!planStarted && (
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-primary to-violet-600 hover:shadow-xl hover:shadow-primary/20 text-white rounded-xl px-8 font-bold"
                  onClick={handleStartPlan}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Start Learning Path
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserLearningPathPage;
